import { useFormikContext } from 'formik'
import { Editor } from 'primereact/editor'
import React, { useEffect, useRef, useState } from 'react'
import DropdownField from '../../../shared/DropdownField'
import useFetchData from '../../../hooks/UseFetchData'
import { useSelector } from 'react-redux'
import HeartIcon from '../../../shared/HeartIcon'
import { extractTextFromHTML } from '../../../utils/ExtractTextFromHtml'
import { t } from 'i18next'
import { useLocation } from 'react-router-dom'
import { BsStars } from 'react-icons/bs'
import toast from 'react-hot-toast'
import { fetchData } from '../../../helpers/fetchData'
import moment from 'moment'

const ProgressNotesEditor = ({ type, placeholder = '' }) => {
  const { values, setFieldValue, errors } = useFormikContext()
  const { data, loading, executeFetch } = useFetchData()
  const [aiLoading, setAiLoading] = useState(false)
  const [aiLoadingStep, setAiLoadingStep] = useState('')
  const typingTimerRef = useRef(null)
  const fieldUpdateTimerRef = useRef(null)
  const auth = useSelector(state => state.auth)
  const { state } = useLocation()
  const visitData = state?.visitData || state?.rowData
  const getDocCheif = async () => {
    let url = ''
    switch (type) {
      case 'significantSign':
        url = '/v3/practitionerSignificantSign/list'
        break
      case 'otherCondition':
        url = '/v3/practitionerOtherCondition/list'
        break
      case 'physical_assesment':
        url = '/v3/practitionerPhysicalAssessment/list'
        break
      case 'past_history':
        url = '/v3/practitionerPastHistory/list'
        break
      case 'family_history':
        url = '/v3/practitionerFamilyHistory/list'
        break
      default:
        url = '/v3/practitionerComplaint/list'
    }

    await executeFetch('POST', url, {
      practitioner_id:
        auth?.roleData?.practitioner_id || visitData?.practitioner_id
    })
  }

  const initialValue =
    type == 'chiefComplaints.value'
      ? values?.chiefComplaints?.value
      : values[type] || ''
  const [text, setText] = useState(initialValue)

  useEffect(() => {
    if (type == 'chiefComplaints.value' && !text) {
      setText(values?.chiefComplaints?.value)
    } else if (
      type == 'chiefComplaints.value' &&
      values?.isUpdatechiefComplaints
    ) {
      setText(values?.chiefComplaints?.value)
      setFieldValue('isUpdatechiefComplaints', false)
    }
  }, [values?.chiefComplaints?.value])

  useEffect(() => {
    if (
      [
        'chiefComplaints.value',
        'significantSign',
        'otherCondition',
        'physical_assesment',
        'past_history',
        'family_history'
      ].includes(type)
    ) {
      getDocCheif()
    }
  }, [])

  useEffect(() => {
    return () => {
      if (typingTimerRef.current) {
        clearTimeout(typingTimerRef.current)
      }
      if (fieldUpdateTimerRef.current) {
        clearTimeout(fieldUpdateTimerRef.current)
      }
    }
  }, [])

  const updateFieldValue = (fieldName, value, debounce = true) => {
    if (fieldUpdateTimerRef.current) {
      clearTimeout(fieldUpdateTimerRef.current)
    }

    if (!debounce) {
      setFieldValue(fieldName, value)
      return
    }

    fieldUpdateTimerRef.current = setTimeout(() => {
      setFieldValue(fieldName, value)
    }, 250)
  }

  const getFavKey = () => {
    switch (type) {
      case 'significantSign':
        return 'is_significant_sign'
      case 'otherCondition':
        return 'is_fav_other_conditions'
      case 'physical_assesment':
        return 'is_fav_physical_assessment'
      case 'past_history':
        return 'is_fav_past_history'
      case 'family_history':
        return 'is_fav_family_history'
      default:
        return 'is_chief_complaint'
    }
  }

  const formatOptions = data => {
    return data?.map(item => {
      if (type === 'significantSign') {
        return {
          label: extractTextFromHTML(item.significient_signs),
          value: item.significient_signs
        }
      } else if (type === 'otherCondition') {
        return {
          label: extractTextFromHTML(item.other_conditions),
          value: item.other_conditions
        }
      } else if (type === 'physical_assesment') {
        return {
          label: extractTextFromHTML(item.physical_assessment),
          value: item.physical_assessment
        }
      } else if (type === 'past_history') {
        return {
          label: extractTextFromHTML(item.past_history),
          value: item.past_history
        }
      } else if (type === 'family_history') {
        return {
          label: extractTextFromHTML(item.family_history),
          value: item.family_history
        }
      }
      return {
        label: extractTextFromHTML(item.chief_complaint?.value),
        value: item.chief_complaint?.value
      }
    })
  }

  const getLabel = () => {
    switch (type) {
      case 'significantSign':
        return { saved: t('Significant Signs'), single: t('significant sign') }
      case 'otherCondition':
        return { saved: t('Other Conditions'), single: t('other condition') }
      case 'physical_assesment':
        return {
          saved: t('Physical Assessments'),
          single: t('physical assessment')
        }
      case 'past_history':
        return { saved: t('Past Histories'), single: t('past history') }
      case 'family_history':
        return { saved: t('Family Histories'), single: t('family history') }
      default:
        return { saved: t('Chief Complaints'), single: t('chief complaint') }
    }
  }

  const labels = getLabel()

  const getDurationOfIllness = () => {
    const duration = values?.vitalSigns?.find(
      item => item?.category === 'duration-of-illness'
    )

    if (!duration?.value) return ''

    return [duration.value, duration.duration_types || duration.valueUnit]
      .filter(Boolean)
      .join(' ')
  }

  const getItemDescription = item =>
    item?.standard_description ||
    item?.non_standard_description ||
    item?.description ||
    item?.name ||
    ''

  const hasDiagnosis = Boolean(values?.diagnosisArray?.length)

  const buildComplaintPayload = () => ({
    visit_date: values?.start_date
      ? moment(values.start_date).format('YYYY-MM-DD')
      : '',
    department:
      values?.practitioner?.speciality?.name_en ||
      values?.practitioner?.speciality?.name ||
      values?.qualification ||
      '',
    diagnoses:
      values?.diagnosisArray?.map(diagnosis => ({
        codeId: diagnosis?.codeId || '',
        codeDescription: diagnosis?.codeDescription || '',
        type: diagnosis?.type || ''
      })) || [],
    pain_score:
      values?.pain_assessment?.pain_score === null ||
      values?.pain_assessment?.pain_score === undefined
        ? ''
        : String(values?.pain_assessment?.pain_score),
    duration_of_illness: getDurationOfIllness(),
    chief_complaint_raw: extractTextFromHTML(
      text || values?.chiefComplaints?.value || ''
    ),
    significant_signs: values?.significantSign || '',
    past_history: values?.past_history || '',
    history_notes: values?.history_notes || '',
    progress_notes: values?.progress_notes || '',
    items:
      values?.items?.map(item => ({
        service_code:
          item?.standard_code ||
          item?.non_standard_code ||
          item?.service_code ||
          '',
        description: getItemDescription(item),
        quantity: item?.quantity || 1,
        body_site: item?.body_site || ''
      })) || []
  })

  const extractGeneratedComplaint = response => {
    const data = response?.data || response

    if (typeof data === 'string') return data

    const candidates = [
      data?.chief_complaint?.value,
      data?.chiefComplaint?.value,
      data?.complaint?.value,
      data?.data?.chief_complaint?.value,
      data?.data?.chiefComplaint?.value,
      data?.data?.complaint?.value,
      data?.chief_complaint,
      data?.chiefComplaint,
      data?.chief_complaint_raw,
      data?.complaint,
      data?.content,
      data?.text,
      data?.result,
      data?.message,
      data?.data?.chief_complaint,
      data?.data?.chiefComplaint,
      data?.data?.complaint,
      data?.data?.content,
      data?.data?.text,
      data?.data?.result
    ]

    return candidates.find(item => typeof item === 'string' && item) || ''
  }

  const typeChiefComplaint = generatedComplaint => {
    const visibleComplaint = generatedComplaint?.includes('<')
      ? extractTextFromHTML(generatedComplaint)
      : generatedComplaint
    const animatedComplaint = visibleComplaint.replace(/\n/g, '<br/>')
    const charsPerStep = 4
    const stepDelay = 45

    return new Promise(resolve => {
      let currentIndex = 0

      setText('')
      updateFieldValue('chiefComplaints.value', '', false)
      setAiLoadingStep(t('Writing chief complaint...'))

      const typeNext = () => {
        currentIndex = Math.min(
          currentIndex + charsPerStep,
          animatedComplaint.length
        )

        const nextValue = animatedComplaint.slice(0, currentIndex)
        setText(nextValue)

        if (currentIndex < animatedComplaint.length) {
          typingTimerRef.current = setTimeout(typeNext, stepDelay)
          return
        }

        setText(generatedComplaint)
        updateFieldValue('chiefComplaints.value', generatedComplaint, false)
        updateFieldValue('isUpdatechiefComplaints', true, false)
        resolve()
      }

      typeNext()
    })
  }

  const handleGenerateChiefComplaint = async () => {
    if (!hasDiagnosis) {
      toast.error(t('Please add diagnosis first'))
      return
    }

    const loadingSteps = [
      t('Generating...'),
      t('Analyzing diagnosis...'),
      t('Reviewing clinical notes...'),
      t('Writing chief complaint...')
    ]
    let stepIndex = 0
    let loadingTimer

    try {
      setAiLoading(true)
      setAiLoadingStep(loadingSteps[stepIndex])
      loadingTimer = setInterval(() => {
        stepIndex = Math.min(stepIndex + 1, loadingSteps.length - 1)
        setAiLoadingStep(loadingSteps[stepIndex])
      }, 1400)

      const response = await fetchData(
        'POST',
        '/v3/ai/cdss/complaint',
        buildComplaintPayload()
      )
      const generatedComplaint = extractGeneratedComplaint(response)

      if (!generatedComplaint) {
        toast.error(t('No chief complaint generated'))
        return
      }

      await typeChiefComplaint(generatedComplaint)
    } finally {
      if (loadingTimer) clearInterval(loadingTimer)
      setAiLoadingStep('')
      setAiLoading(false)
    }
  }

  const headerTemplate = (
    <div className='flex w-full items-center justify-between gap-2 overflow-hidden'>
      <span className='ql-formats flex min-w-0 items-center gap-2'>
        <select className='ql-header' defaultValue='0'>
          <option value='1'>{t('Heading')}</option>
          <option value='2'>{t('Subheading')}</option>
          <option value='0'>{t('Normal')}</option>
        </select>
        <button className='ql-bold'></button>
        <button className='ql-italic'></button>
      </span>
      <div className='flex items-center gap-2'>
        {[
          'chiefComplaints.value',
          'significantSign',
          'otherCondition',
          'physical_assesment',
          'past_history',
          'family_history'
        ].includes(type) && (
          <div className='flex shrink-0 items-center gap-2'>
            <div className='flex items-center gap-2'>
              {type !== 'chiefComplaints.value' && (
                <span className='text-sm whitespace-nowrap'>
                  {t('Add to Favorites')} :
                </span>
              )}
              <HeartIcon valueText={getFavKey()} />
            </div>
            {type === 'chiefComplaints.value' && (
              <div
                role='button'
                tabIndex={aiLoading || !hasDiagnosis ? -1 : 0}
                onClick={() => {
                  if (!aiLoading && hasDiagnosis) {
                    handleGenerateChiefComplaint()
                  }
                }}
                onKeyDown={event => {
                  if (
                    (event.key === 'Enter' || event.key === ' ') &&
                    !aiLoading &&
                    hasDiagnosis
                  ) {
                    event.preventDefault()
                    handleGenerateChiefComplaint()
                  }
                }}
                title={
                  hasDiagnosis
                    ? t('Generate chief complaint')
                    : t('Please add diagnosis first')
                }
                className={`inline-flex h-[28px] w-[172px] shrink-0 items-center justify-center gap-1 whitespace-nowrap rounded-md border border-primary bg-primary px-2 text-[10px] font-extrabold leading-none text-white shadow-sm transition-colors duration-200 hover:bg-[#0068b8] focus:outline-none focus:ring-2 focus:ring-primary/30 ${
                  aiLoading || !hasDiagnosis
                    ? 'cursor-not-allowed opacity-60'
                    : 'cursor-pointer'
                }`}
              >
                <BsStars
                  className={aiLoading ? 'animate-spin' : ''}
                  size={13}
                />
                <span className='text-center font-extrabold tracking-normal drop-shadow-sm'>
                  {aiLoading
                    ? aiLoadingStep || t('Generating...')
                    : t('Generate chief complaint')}
                </span>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )

  return (
    <div className='mt-2'>
      <div>
        {[
          'chiefComplaints.value',
          'significantSign',
          'otherCondition',
          'physical_assesment',
          'past_history',
          'family_history'
        ].includes(type) && (
          <div>
            <div className='mb-1 flex items-center justify-between gap-3'>
              <label className='input-label'>
                {`${t('Saved')} ${labels.saved}`}
              </label>
            </div>
            <DropdownField
              placeholder={`${t('Select a saved')} ${labels.single}`}
              filter
              useFormik={false}
              options={formatOptions(data?.data)}
              handleChange={e => {
                const combinedValue = text ? `${text}<br/>${e.value}` : e.value
                setText(combinedValue)
                updateFieldValue(type, combinedValue, false)
              }}
            />
          </div>
        )}
        <Editor
          value={text}
          onTextChange={e => {
            setText(e.htmlValue)
            updateFieldValue(type, e.htmlValue)
          }}
          style={{ height: '200px' }}
          headerTemplate={headerTemplate}
          className='mt-2'
          placeholder={placeholder}
        />
        {/* {type === "chiefComplaints.value" && errors?.chiefComplaints?.value && (
          <p className="text-sm text-red-500 mt-1">
            {errors.chiefComplaints.value}
          </p>
        )} */}
      </div>
    </div>
  )
}

export default React.memo(ProgressNotesEditor)
